/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useServiceContext } from '../../../root'
import type { Tag } from '../../../services/api/api.tags'
import { createDateTimeFormatter } from '../../../utils/commonColumns'
import TableMenu from './TableMenu'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'

interface TagsTableProps {
  onEditTag: (tag: Tag) => void
  onDeleteSuccess?: () => void
}

const Table: React.FC<TagsTableProps> = ({ onEditTag, onDeleteSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  const handleRowClick = (row: Tag) => {
    // Only allow clicking on non-system and non-regulatory submission tags
    if (row.type !== 'SYSTEM' && row.type !== 'REGULATORY_SUBMISSION') {
      onEditTag(row)
    }
  }

  const isEditable = (row: Tag): boolean => {
    return row.type !== 'SYSTEM' && row.type !== 'REGULATORY_SUBMISSION'
  }

  return (
    <RcmTable
      className="chita-table"
      noResultsText={translate('NO_RESULTS_FILTERED')}
      HeadRow={
        <TableHeadRow className="has-actions">
          <TableHeadCell style={{ fontSize: '12px' }} sort="NAME">
            {translate('NAME')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="TYPE">
            {translate('TAG_TYPE')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="CREATED_AT">
            {translate('CREATED_ON')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="CREATED_BY">
            {translate('CREATED_BY')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="UPDATED_AT">
            {translate('TABLE_TH_MODIFIED_ON')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="UPDATED_BY">
            {translate('TABLE_TH_MODIFIED_BY')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} id="ea9636ea-d8dc-420d-9257-7530147b4e8a" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }: { row: Tag }) => {
        return (
          <TableRow key={row.id}>
            <td className="clipped-cell">
              <div className="ellipsis">
                {!isEditable(row) ? (
                  row.name
                ) : (
                  <span className="btn-link" onClick={() => handleRowClick(row)} style={{ cursor: 'pointer' }}>
                    {row.name}
                  </span>
                )}
              </div>
            </td>
            <td>{translate('SETTINGS_TAG_TYPE_' + row.type)}</td>
            <td>{formatDateTime(row.createdAt)}</td>
            <td>{row.createdBy && row.createdBy.name ? row.createdBy.name : translate('NOT_AVAILABLE')}</td>
            <td>{formatDateTime(row.updatedAt)}</td>
            <td>{row.updatedBy && row.updatedBy.name ? row.updatedBy.name : translate('NOT_AVAILABLE')}</td>
            <td className="actions">
              {isEditable(row) && <TableMenu item={row} onEditTag={onEditTag} onDeleteSuccess={onDeleteSuccess} />}
            </td>
          </TableRow>
        )
      }}
    />
  )
}

export default Table
